import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const emptyConfig = {
  companyname: "",
  branchname: "",
  finyearfromdate: "",
  finyeartodate: "",
  tally_subledger_group_name: "",
  tally_subledger_group_suffix: "",
  tally_sale_account_name: "",
  tally_cgst_sgst_account_name: "",
  tally_igst_account_name: "",
  tally_item_ledger_name: "",
  tally_voucher_prefix: "",
};

const subledgerFields = [
  {
    key: "tally_subledger_group_name",
    label: "Subledger Group Name",
    placeholder: "SUNDRY DEBTORS (HONDA KOL)",
  },
  {
    key: "tally_subledger_group_suffix",
    label: "Subledger Group Suffix",
    placeholder: "-KOL",
  },
];

const saleFields = [
  {
    key: "tally_sale_account_name",
    label: "Sale Account Name",
    placeholder: "GST SALES",
  },
  {
    key: "tally_cgst_sgst_account_name",
    label: "CGST-SGST Account Name",
    placeholder: "SGST-CGST OUTWARD",
  },
  {
    key: "tally_igst_account_name",
    label: "IGST Account Name",
    placeholder: "IGST OUTWARD",
  },
  {
    key: "tally_item_ledger_name",
    label: "Item Ledger Name",
    placeholder: "SPARES, LUBRICANT, TYRE",
  },
  {
    key: "tally_voucher_prefix",
    label: "Bill Number Prefix",
    placeholder: "KOL-",
  },
];

function suggestedToDate(finyearToDate) {
  const today = new Date().toISOString().slice(0, 10);
  return finyearToDate && finyearToDate < today ? finyearToDate : today;
}

async function responseMessage(response, fallback) {
  try {
    const data = await response.json();
    return data.message || data.error || fallback;
  } catch (error) {
    return fallback;
  }
}

const TallyExport = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const [mode, setMode] = useState("subledger");
  const [config, setConfig] = useState(emptyConfig);
  const [savedConfig, setSavedConfig] = useState(emptyConfig);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fromdate, setFromdate] = useState("");
  const [todate, setTodate] = useState("");

  const fields = useMemo(
    () => (mode === "subledger" ? subledgerFields : saleFields),
    [mode]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      try {
        setLoading(true);
        const response = await fetch("/tally-export/config", {
          headers: { sptoken: userToken },
        });
        if (!response.ok) {
          throw new Error(await responseMessage(response, "Unable to load Tally settings."));
        }
        const data = await response.json();
        if (cancelled) return;
        const nextConfig = { ...emptyConfig, ...data };
        setConfig(nextConfig);
        setSavedConfig(nextConfig);
        setFromdate(data.finyearfromdate || "");
        setTodate(suggestedToDate(data.finyeartodate));
      } catch (error) {
        if (!cancelled) toast.error(error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (userToken) loadConfig();
    return () => {
      cancelled = true;
    };
  }, [userToken]);

  if (!userToken) return <Redirect exact to="/sparepartsLogin" />;

  const handleModeChange = (event) => {
    setMode(event.target.value);
    setConfig(savedConfig);
    setEditing(false);
  };

  const handleFieldChange = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const cancelEdit = () => {
    setConfig(savedConfig);
    setEditing(false);
  };

  const updateSettings = async () => {
    const missing = fields.find(({ key }) => !String(config[key] || "").trim());
    if (missing) {
      toast.error(`${missing.label} is required.`);
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`/tally-export/config/${mode === "subledger" ? "subledger" : "sale"}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(config),
      });
      if (!response.ok) {
        throw new Error(await responseMessage(response, "Unable to update Tally settings."));
      }
      setSavedConfig(config);
      setEditing(false);
      toast.success("Tally settings updated.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadResponse = async (response, fallbackName) => {
    if (!response.ok) {
      throw new Error(await responseMessage(response, "Unable to create the Tally export."));
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = match ? match[1] : fallbackName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return response.headers.get("X-Exported-Records");
  };

  const exportXml = async () => {
    if (mode === "sale" && (!fromdate || !todate || fromdate > todate)) {
      toast.error("Select a valid date range.");
      return;
    }

    try {
      setExporting(true);
      const response =
        mode === "subledger"
          ? await fetch("/tally-export/subledgers.xml", {
              headers: { sptoken: userToken },
            })
          : await fetch("/tally-export/sale-vouchers.xml", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                sptoken: userToken,
              },
              body: JSON.stringify({ fromdate, todate }),
            });
      const count = await downloadResponse(
        response,
        mode === "subledger" ? "tally_subledgers.xml" : "tally_sale_journals.xml"
      );
      toast.success(`${count || "Tally"} ${mode === "subledger" ? "subledgers" : "journals"} exported.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Sidebar
      Title="TALLY EXPORT"
      expandedPanel="panel4"
      children={
        <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Tally Export
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {config.companyname} / {config.branchname}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {mode === "sale" && <Chip label="Journal" color="primary" size="small" />}
              <Chip
                icon={<LockIcon />}
                label={editing ? "Editing" : "Settings locked"}
                color={editing ? "warning" : "default"}
                variant="outlined"
                size="small"
              />
            </Stack>
          </Stack>

          <Box sx={{ borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", py: 2 }}>
            <FormControl size="small" sx={{ width: { xs: "100%", sm: 280 } }}>
              <InputLabel id="tally-export-type-label">Export Type</InputLabel>
              <Select
                labelId="tally-export-type-label"
                value={mode}
                label="Export Type"
                onChange={handleModeChange}
                disabled={loading || saving || exporting}
              >
                <MenuItem value="subledger">Subledger</MenuItem>
                <MenuItem value="sale">Sale Voucher</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ py: 3 }}>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={30} />
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  {fields.map(({ key, label, placeholder }) => (
                    <TextField
                      key={key}
                      label={label}
                      value={config[key]}
                      placeholder={placeholder}
                      onChange={(event) => handleFieldChange(key, event.target.value)}
                      disabled={!editing}
                      size="small"
                      fullWidth
                      inputProps={{ maxLength: key === "tally_voucher_prefix" ? 30 : 150 }}
                    />
                  ))}
                </Box>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {!editing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEditing(true)}
                    >
                      Change
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="text"
                        startIcon={<CloseIcon />}
                        onClick={cancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={updateSettings}
                        disabled={saving}
                      >
                        Update
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>

          <Divider />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ py: 3 }}
          >
            {mode === "sale" && (
              <>
                <TextField
                  label="From Date"
                  type="date"
                  value={fromdate}
                  onChange={(event) => setFromdate(event.target.value)}
                  inputProps={{ min: config.finyearfromdate, max: config.finyeartodate }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: { xs: "100%", md: 210 } }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  value={todate}
                  onChange={(event) => setTodate(event.target.value)}
                  inputProps={{ min: config.finyearfromdate, max: config.finyeartodate }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: { xs: "100%", md: 210 } }}
                />
              </>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={exportXml}
              disabled={loading || editing || exporting}
              sx={{ ml: { md: "auto !important" }, minWidth: 170 }}
            >
              {exporting ? "Exporting..." : "Export XML"}
            </Button>
          </Stack>
        </Box>
      }
    />
  );
};

export default TallyExport;
